import { useState, type ChangeEvent, type FormEvent } from 'react';
import {
  apiErrorText,
  type BankCredentialsInput,
  type CeoBankConnection,
  type CeoBankConnectResult,
  useCreateCeoBankConnectionMutation,
  useReplaceCeoBankConnectionSecretMutation,
} from '@shared/api';
import { Button } from '@shared/ui/Button';
import { WrapButton } from '../ui';
import { BANK_PROVIDER_NAMES } from '../ceoFormat';
import {
  type CertificateContainer,
  isCertificateContainer,
  readCertificateContainer,
  readCertificateFile,
} from './certificateFile';
import {
  CheckLabel,
  ErrorNotice,
  FormActions,
  FormBox,
  FormGrid,
  FormHint,
  FormTitle,
  HintSteps,
  Input,
  Select,
  StatementTextarea,
} from './styles';

type Provider = CeoBankConnection['provider'];
type AlfaAccess = 'login' | 'key';
type SberAccess = 'tokens' | 'login';

/** Страница возврата из банка. Этот же адрес вносится в настройки приложения в банке. */
export const BANK_RETURN_PATH = '/agents/finance/accounts';

const TOCHKA_INSTRUCTION =
  'Интернет-банк Точки → «Сервисы» → «Интеграции и API» → «Создать JWT-ключ». Разрешения: счета, остатки, выписки, сведения о клиенте. Права на платежи не включайте. Один ключ видит все компании этого пользователя.';

const SBER_INTRO =
  'В письме банка только Client ID и scope. Client Secret и сертификат письмом не приходят: их выпускают в кабинете.';

const SBER_CABINET_STEPS = [
  'Откройте СберБизнес → «Все продукты и услуги» → Sber API → карточку сервиса. Если есть кнопка «Активировать», нажмите её.',
  'Client Secret: у поля Client_secret нажмите «Обновить», затем «Сгенерировать». Он показывается один раз, сразу вставьте его сюда.',
  'Сертификат: блок «Сертификаты шифрования» → «Сгенерировать сертификат». Задайте пароль, скачайте файл .p12 и выберите его ниже.',
];

const SBER_LOGIN_STEP =
  'Нажмите кнопку внизу формы: откроется вход в СберБизнес. Войдите сотрудником с правом подписи.';

const SBER_TOKENS_STEP =
  'Refresh-токен: блок «Ключи доступа» → «Сгенерировать ключ», подтвердите СМС-кодом. Вход в банк не нужен.';

const SBER_RETURN_HINT = 'Redirect URI в карточке: значение https:// подходит, другое замените на ';

const SBER_SANDBOX_HINT =
  'Для песочницы всё то же самое берётся на вкладке «Песочница API» этого сервиса: Client ID, сертификат и токены там свои.';

const ALFA_INSTRUCTIONS: Record<AlfaAccess, string> = {
  login:
    'Банк выдаёт Client ID и клиентский сертификат: сертификат и закрытый ключ. Client Secret ОСНОВА получит у банка сама. Права в настройках приложения: openid, transactions, customer.',
  key: 'Нужны API-ключ с портала разработчика Alfa API и клиентский сертификат: сертификат и закрытый ключ. Права — только выписки и сведения об организации.',
};

const ALFA_RETURN_HINT =
  ' После сохранения откроется вход в Альфа-Бизнес. Адрес возврата в настройках приложения: ';

const CERTIFICATE_HINT =
  'Выберите файл .p12 или файлы сертификата и закрытого ключа: можно все сразу и вместе с цепочкой банка, порядок не важен. Или вставьте их текст.';

const SBER_CERTIFICATE_HINT = 'Файл .p12 из кабинета и пароль, который вы задали при выпуске.';

interface ConnectBankFormProps {
  /** Замена ключа существующего подключения. Без него — новое подключение. */
  connection?: CeoBankConnection;
  onDone: (result: CeoBankConnectResult) => void;
  onClose: () => void;
}

export function ConnectBankForm({ connection, onDone, onClose }: ConnectBankFormProps) {
  const [create, { isLoading: isCreating }] = useCreateCeoBankConnectionMutation();
  const [replace, { isLoading: isReplacing }] = useReplaceCeoBankConnectionSecretMutation();
  const [provider, setProvider] = useState<Provider>(connection?.provider ?? 'tochka_api');
  const [label, setLabel] = useState('');
  const [token, setToken] = useState('');
  const [certificate, setCertificate] = useState('');
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [refreshToken, setRefreshToken] = useState('');
  const [container, setContainer] = useState<CertificateContainer | null>(null);
  const [containerPassword, setContainerPassword] = useState('');
  const [alfaAccess, setAlfaAccess] = useState<AlfaAccess>('login');
  const [sberAccess, setSberAccess] = useState<SberAccess>(
    connection && connection.status !== 'pending' ? 'tokens' : 'login',
  );
  const [sandbox, setSandbox] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isReplace = Boolean(connection);
  const isAlfa = provider === 'alfa_api';
  const isSber = provider === 'sber_business';
  const isAlfaLogin = isAlfa && alfaAccess === 'login';
  const isSberTokens = isSber && sberAccess === 'tokens';
  const usesClientId = isAlfaLogin || isSber;
  const usesLogin = usesClientId && !isSberTokens;
  const canUseSandbox = isAlfaLogin || isSberTokens;
  const isSandbox = canUseSandbox && (connection ? connection.sandbox : sandbox);
  const isBusy = isCreating || isReplacing;
  const returnUrl = `${window.location.origin}${BANK_RETURN_PATH}`;
  const keepHint = isReplace && provider !== 'tochka_api';

  /** Ключ другого банка, способа или контура не должен уйти вместе с формой. */
  function clearKeys() {
    setToken('');
    setClientSecret('');
    setRefreshToken('');
  }

  function chooseProvider(next: Provider) {
    clearKeys();
    setClientId('');
    setCertificate('');
    setContainer(null);
    setContainerPassword('');
    setProvider(next);
  }

  async function handleCertificateFiles(e: ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    e.target.value = '';
    try {
      setError(null);
      const containers = files.filter(isCertificateContainer);
      if (containers.length > 1) {
        setError('Выберите один файл .p12');
        return;
      }
      if (containers[0]) {
        setContainer(await readCertificateContainer(containers[0]));
        setContainerPassword('');
      }
      const pemFiles = files.filter((file) => !isCertificateContainer(file));
      const parts = await Promise.all(pemFiles.map(readCertificateFile));
      setCertificate((text) => [text.trim(), ...parts].filter(Boolean).join('\n'));
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Файл не читается');
    }
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (isSber && !isReplace && !container && !certificate.trim()) {
      setError('Выберите файл сертификата .p12: как его выпустить, написано в шаге 3');
      return;
    }
    const credentials: BankCredentialsInput = {
      token: usesClientId ? undefined : token.trim() || undefined,
      certificate: certificate.trim() || undefined,
      certificate_p12: container?.base64,
      certificate_password: container ? containerPassword : undefined,
      client_id: usesClientId ? clientId.trim() || undefined : undefined,
      client_secret: usesClientId ? clientSecret.trim() || undefined : undefined,
      refresh_token: isSberTokens ? refreshToken.trim() || undefined : undefined,
      redirect_uri: usesClientId ? returnUrl : undefined,
      sandbox: canUseSandbox && sandbox ? true : undefined,
    };
    try {
      setError(null);
      const result = connection
        ? await replace({ id: connection.id, ...credentials }).unwrap()
        : await create({ provider, label: label.trim(), ...credentials }).unwrap();
      onDone(result);
    } catch (err: unknown) {
      setError(apiErrorText(err, 'Банк не принял данные подключения'));
    }
  }

  return (
    <FormBox as="form" onSubmit={handleSubmit}>
      <FormTitle>
        {connection
          ? `Заменить ключ: ${BANK_PROVIDER_NAMES[provider]}, ${connection.label}`
          : 'Подключить банк'}
      </FormTitle>
      {!connection && (
        <FormGrid>
          <Select value={provider} onChange={(e) => chooseProvider(e.target.value as Provider)}>
            <option value="tochka_api">{BANK_PROVIDER_NAMES.tochka_api}</option>
            <option value="alfa_api">{BANK_PROVIDER_NAMES.alfa_api}</option>
            <option value="sber_business">{BANK_PROVIDER_NAMES.sber_business}</option>
          </Select>
          <Input
            placeholder="Компания (напр. АО «САТКОМ»)"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            required
          />
        </FormGrid>
      )}
      {isAlfa && !connection?.sandbox && (
        <Select
          value={alfaAccess}
          onChange={(e) => {
            clearKeys();
            setAlfaAccess(e.target.value as AlfaAccess);
          }}
        >
          <option value="login">Client ID и вход в Альфа-Бизнес</option>
          <option value="key">API-ключ</option>
        </Select>
      )}
      {isSber && !connection && (
        <Select
          value={sberAccess}
          onChange={(e) => {
            clearKeys();
            setSberAccess(e.target.value as SberAccess);
          }}
        >
          <option value="login">Вход в СберБизнес</option>
          <option value="tokens">Токены из блока «Ключи доступа»</option>
        </Select>
      )}
      {isSber ? (
        <FormHint>
          {SBER_INTRO}
          <HintSteps>
            {SBER_CABINET_STEPS.map((step) => (
              <li key={step}>{step}</li>
            ))}
            <li>{isSberTokens ? SBER_TOKENS_STEP : SBER_LOGIN_STEP}</li>
          </HintSteps>
          {isSandbox && SBER_SANDBOX_HINT}
          {usesLogin && (
            <>
              {SBER_RETURN_HINT}
              <strong>{returnUrl}</strong>
            </>
          )}
        </FormHint>
      ) : (
        <FormHint>
          {isAlfa ? ALFA_INSTRUCTIONS[alfaAccess] : TOCHKA_INSTRUCTION}
          {isAlfaLogin && !isSandbox && (
            <>
              {ALFA_RETURN_HINT}
              <strong>{returnUrl}</strong>
            </>
          )}
        </FormHint>
      )}
      {keepHint && <FormHint>Поле, которое не меняется, оставьте пустым.</FormHint>}
      {canUseSandbox && !connection && (
        <CheckLabel>
          <input
            type="checkbox"
            checked={sandbox}
            onChange={(e) => {
              clearKeys();
              setSandbox(e.target.checked);
            }}
          />
          Тестовый контур банка (песочница). Проверит доступ, счета и выписку. Данные там учебные,
          в учёт они не записываются.
        </CheckLabel>
      )}
      {usesClientId && (
        <FormGrid>
          <Input
            placeholder="Client ID"
            value={clientId}
            onChange={(e) => setClientId(e.target.value)}
            autoComplete="off"
            required={!isReplace}
          />
          <Input
            type="password"
            placeholder={isAlfa ? 'Client Secret, если банк его выдал' : 'Client Secret'}
            value={clientSecret}
            onChange={(e) => setClientSecret(e.target.value)}
            autoComplete="new-password"
            required={!isAlfa && !isReplace}
          />
        </FormGrid>
      )}
      {isSberTokens && (
        <Input
          type="password"
          placeholder="Refresh-токен из блока «Ключи доступа»"
          value={refreshToken}
          onChange={(e) => setRefreshToken(e.target.value)}
          autoComplete="new-password"
          required={!isReplace}
        />
      )}
      {!usesClientId && (
        <Input
          type="password"
          placeholder={provider === 'tochka_api' ? 'JWT-ключ' : 'API-ключ Alfa API'}
          value={token}
          onChange={(e) => setToken(e.target.value)}
          autoComplete="new-password"
          required={provider === 'tochka_api' || !isReplace}
        />
      )}
      {provider !== 'tochka_api' && (
        <>
          <Input
            type="file"
            accept=".p12,.pfx,.pem,.crt,.cer,.der,.key"
            multiple
            aria-label="Файлы сертификата и закрытого ключа"
            onChange={handleCertificateFiles}
          />
          {container && (
            <FormGrid>
              <Input value={container.name} readOnly aria-label="Выбранный файл .p12" />
              <Input
                type="password"
                placeholder="Пароль файла .p12"
                value={containerPassword}
                onChange={(e) => setContainerPassword(e.target.value)}
                autoComplete="new-password"
              />
            </FormGrid>
          )}
          {(isAlfa || certificate !== '') && (
            <StatementTextarea
              rows={5}
              placeholder="Сертификат и закрытый ключ в формате PEM"
              value={certificate}
              onChange={(e) => setCertificate(e.target.value)}
              spellCheck={false}
              required={!isReplace && !container}
            />
          )}
          <FormHint>{isSber ? SBER_CERTIFICATE_HINT : CERTIFICATE_HINT}</FormHint>
        </>
      )}
      <FormHint>
        Ключ хранится зашифрованным и обратно не показывается. Банк используется только для чтения:
        платежи отсюда не отправляются.
      </FormHint>
      {error && <ErrorNotice>{error}</ErrorNotice>}
      <FormActions>
        <WrapButton $size="sm" type="submit" disabled={isBusy}>
          {isBusy
            ? 'Проверяем у банка…'
            : isSandbox && isAlfa
              ? 'Сохранить и перейти ко входу'
              : usesLogin
                ? `Сохранить и войти в ${isAlfaLogin ? 'Альфа-Бизнес' : 'СберБизнес'}`
                : 'Проверить и подключить'}
        </WrapButton>
        <Button $size="sm" $variant="ghost" type="button" onClick={onClose}>
          Отмена
        </Button>
      </FormActions>
    </FormBox>
  );
}
